import styled, { createGlobalStyle } from "styled-components";
import axios from "axios";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "devextreme-react";
import parse from "html-react-parser";
import Profile from "../../common/Profile";
import * as editIcon from "./edit.svg";
import * as replyIcon from "./reply.svg";
import QuoteBox from "./QuoteBox";
import DescrEditor from "./DescrEditor";
import { useSelector } from "react-redux";
import { toastMsg } from "../utils";
import { confirm } from "devextreme/ui/dialog";

const MessageBoxWrapper = styled.div`
  display: flex;
  > div:first-child {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 34px;
  }
  > div:last-child {
    width: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 32px;
  }
`;
const Line = styled.div`
  width: 1px;
  height: 100%;
  background-color: #e6e6ea;
`;
const MessageHeader = styled.div`
  position: relative;
  background: #fafafa;
  color: black;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  &:after {
    content: "";
    position: absolute;
    display: block;
    width: 0;
    z-index: 1;
    border-style: solid;
    border-color: transparent #fafafa;
    border-width: 8px 24px 8px 0;
    top: 50%;
    left: -24px;
    margin-top: -8px;
  }

  display: flex;
  justify-content: space-between;
  padding: 8px 8px 8px 16px;
  > div:first-child {
    display: flex;
    align-items: center;
    span {
      font-weight: bold;
      margin-right: 5px;
    }
  }
  .dx-button.dx-button-has-icon:not(.dx-button-has-text):not(.dx-shape-standard) {
    min-width: 26px;
    border-radius: 5px;
    margin: 0;
    float: left;
    background-color: #fafafa;
    i {
      font-size: 18px;
      font-weight: bold;
    }
    img {
      width: 16px;
      height: 16px;
    }
  }
  .dx-button-has-icon .dx-button-content {
    padding: 0;
  }
  .dx-state-hover {
    background-color: #ebebea !important;
  }
`;
const MessageBody = styled.div`
  border: 1px solid #fafafa;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  padding: 8px 16px;
  img {
    max-width: 800px;
  }
  table {
    width: 100%;
    table-layout: fixed;
    word-break: break-all;
    height: auto;
    td {
      background-color: #fafafa;
      padding: 8px 16px;
      p {
        margin: 0;
      }
    }
    tr:nth-child(2n) {
      td {
        background-color: white;
      }
    }
  }
`;
const ButtonArea = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  #submitBtn,
  #cancelBtn {
    margin: 16px 0px 0px 16px;
    font-size: 16px;
    border-radius: 10px;
    border: 3px solid #7fba4c;
    padding: 3px 0px;
    &:not(.dx-button-mode-outlined) {
      background-color: #7fba4c;
    }
    .dx-button-text {
      font-weight: bold;
    }
  }
  #cancelBtn .dx-button-text {
    color: black;
  }
`;
const RowBar = styled.div`
  width: 100%;
  height: 1px;
  background-color: black;
`;
const ScrollSmooth = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }
`;

const MessageBox = (props) => {
  const { cmtRef, id, isLastMsg, mode, msgs, questionId, SEQ, setDetailsData, status } = props;
  const [isEditMode, setEditMode] = useState(false);
  const msgRef = useRef(null);
  const userInfo = useSelector((state) => state.userInfo);

  const msg = msgs[SEQ]; // 현재 메시지
  const navigate = useNavigate();
  const options = {
    replace: (domNode) => {
      if (domNode.attribs && domNode.attribs["data-id"] && domNode.attribs.class.includes("quote-box")) {
        const { "data-id": SEQ } = domNode.attribs;
        return <QuoteBox href={`#tl${SEQ}`} msg={msgs[SEQ]} />;
      }
    },
  };

  /**
   *
   * @param {string} login
   * @param {string} au
   * @returns {boolean}
   */
  const isAuthor = (login, au) => {
    return login === au;
  };

  /**
   *
   * @param {string} msg
   * @returns {string | null}
   */
  const extractQuotedSEQ = (msg) => {
    const quoteBoxes = new DOMParser().parseFromString(msg, "text/html").querySelectorAll("span.quote-box");
    const result = [];
    quoteBoxes.forEach((el) => {
      result.push(el.getAttribute("data-id"));
    });
    return result.length ? result.join(",") : null;
  };

  const onCancelUpdate = () => {
    setEditMode(false);
  };

  const onClickDelete = async () => {
    const result = await confirm(`${SEQ === 1 ? "게시글을" : "답글을"} 삭제하시겠습니까?`, "Delete the message");
    if (result) {
      axios
        .delete(`${process.env.REACT_APP_BECCO_API_URL}/api/bbs/${questionId}/${SEQ}`)
        .then((res) => {
          if (SEQ === 1) {
            toastMsg(`게시글이 삭제되었습니다.`, true);
            // 게시물을 지웠다면 조회페이지로 이동
            navigate("/qna");
          } else {
            toastMsg(`답글이 삭제되었습니다.`, true);
            setDetailsData(res.data);
          }
        })
        .catch((err) => {
          if (err.response.status === 403) {
            toastMsg(`상태가 변경되었습니다. 삭제가 불가능합니다.`, false);
            setDetailsData(err.response.data);
          } else {
            toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
            throw err;
          }
        });
    }
  };

  const onClickEdit = () => {
    setEditMode(true);
  };

  const onClickQuote = () => {
    const index = cmtRef.current.instance.option("value")?.length || 0;
    const type = "variable";
    const descr = msg.descr.replace(/(<([^>]+)>)/gi, "");
    const config = {
      value: `${SEQ}`,
      escapeChar: [`${msg.au},${msg.auName}`, `${descr.length > 50 ? descr.slice(0, 50) + "..." : descr}`],
    };

    cmtRef.current.instance.insertEmbed(index, type, config);
    window.scrollTo({ top: document.body.scrollHeight });
  };

  const onClickUpdate = (e) => {
    const validationResult = e.validationGroup.validators.reduce((res, ele) => {
      if (ele._$element[0].id === `msg${SEQ}Editor`) {
        const { isValid } = ele.validate();
        res = isValid;
      }
      return res;
    }, true);
    const newDescr = msgRef.current.instance.option("value");
    if (validationResult) {
      const dReply = extractQuotedSEQ(newDescr);
      axios
        .put(`${process.env.REACT_APP_BECCO_API_URL}/api/bbs/${questionId}/${SEQ}`, {
          descr: newDescr,
          dReply: dReply, // null, "1", or "1, 2". 인용된 순서.
        })
        .then((res) => {
          setDetailsData(res.data);
        })
        .then(() => {
          toastMsg(`${SEQ === 1 ? "게시글이" : "답글이"} 수정되었습니다.`, true);
          setEditMode(false);
          e.validationGroup.reset();
        })
        .catch((err) => {
          if (err.response.status === 403) {
            toastMsg(`상태가 변경되어 더이상 ${SEQ === 1 ? "게시글" : "답글"} 수정이 불가능합니다.`, false);
            setDetailsData(err.respond.data);
          } else {
            toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
          }
          setEditMode(false);
        });
    }
  };

  return (
    <>
      <ScrollSmooth />
      <MessageBoxWrapper>
        <div>
          <Profile employeeNo={msg.au} />
          {!isLastMsg ? <Line /> : ""}
        </div>
        <div>
          {!isEditMode ? (
            <>
              <MessageHeader id={id}>
                <div>
                  <span>{msg.auName}</span> {SEQ === 1 ? "requested" : "commented"} on {msg.date}
                </div>
                <div>
                  {((SEQ === 1 && isAuthor(userInfo.employeeNo, msg.au) && status === "1") ||
                    (SEQ !== 1 && isAuthor(userInfo.employeeNo, msg.au) && status !== "3" && status !== "4") ||
                    mode === "admin") && (
                    <Button
                      id="editBtn"
                      width={26}
                      height={26}
                      icon={`${editIcon.default}`}
                      stylingMode="contained"
                      onClick={onClickEdit}
                    />
                  )}
                  {((SEQ === 1 &&
                    isAuthor(userInfo.employeeNo, msg.au) &&
                    status === "1" &&
                    Object.keys(msgs).length <= 1) ||
                    (SEQ !== 1 && isAuthor(userInfo.employeeNo, msg.au) && status !== "3" && status !== "4") ||
                    mode === "admin") && (
                    <Button
                      id="deleteBtn"
                      width={26}
                      height={26}
                      icon={"trash"}
                      stylingMode="contained"
                      onClick={onClickDelete}
                    />
                  )}
                  {(status === "1" || status === "2") && (
                    <Button
                      id="quoteBtn"
                      width={26}
                      height={26}
                      icon={`${replyIcon.default}`}
                      stylingMode="contained"
                      onClick={onClickQuote}
                    />
                  )}
                </div>
              </MessageHeader>
              <MessageBody>{parse(msg.descr, options)}</MessageBody>
            </>
          ) : (
            <>
              <DescrEditor cmtRef={msgRef} defaultValue={msg.descr} SEQ={SEQ} />
              <ButtonArea>
                <Button
                  id="submitBtn"
                  height={34}
                  text="수정하기"
                  type="success"
                  stylingMode="contained"
                  onClick={onClickUpdate}
                  width={105}
                />
                <Button
                  id="cancelBtn"
                  height={34}
                  text="취소하기"
                  type="success"
                  stylingMode="outlined"
                  onClick={onCancelUpdate}
                  width={105}
                />
              </ButtonArea>
            </>
          )}
        </div>
      </MessageBoxWrapper>
      {isLastMsg ? <RowBar /> : ""}
    </>
  );
};

export default MessageBox;
